import { useCallback, useMemo, useRef } from "react";

interface Props {
  min: number;
  max: number;
  valueFrom: number;
  valueTo: number;
  onChange: (from: number, to: number) => void;
  step?: number;
}

function computeNiceStep(range: number): number {
  if (range <= 0) return 1;
  const rough = range / 20;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
  const normalized = rough / magnitude;
  if (normalized <= 1) return magnitude;
  if (normalized <= 2) return 2 * magnitude;
  if (normalized <= 5) return 5 * magnitude;
  return 10 * magnitude;
}

export default function RangeSlider({ min, max, valueFrom, valueTo, onChange, step: stepProp }: Props) {
  const trackRef = useRef<HTMLDivElement>(null);

  const range = max - min || 1;
  const step = stepProp ?? computeNiceStep(range);

  const snapPoints = useMemo(() => {
    const points: number[] = [];
    for (let v = min; v <= max; v += step) {
      points.push(v);
    }
    if (points[points.length - 1] < max) {
      points.push(max);
    }
    return points;
  }, [min, max, step]);

  const leftPercent = ((valueFrom - min) / range) * 100;
  const rightPercent = ((valueTo - min) / range) * 100;

  const getValueFromPosition = useCallback(
    function getValueFromPosition(clientX: number) {
      if (!trackRef.current) return min;
      const rect = trackRef.current.getBoundingClientRect();
      const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const raw = min + percent * range;
      let closest = snapPoints[0];
      let minDist = Math.abs(raw - closest);
      for (let i = 1; i < snapPoints.length; i++) {
        const dist = Math.abs(raw - snapPoints[i]);
        if (dist < minDist) {
          minDist = dist;
          closest = snapPoints[i];
        }
      }
      return closest;
    },
    [min, range, snapPoints],
  );

  function handlePointerDown(thumb: "from" | "to") {
    return function onPointerDown(e: React.PointerEvent) {
      e.preventDefault();
      const target = e.currentTarget as HTMLElement;
      target.setPointerCapture(e.pointerId);

      function onPointerMove(ev: PointerEvent) {
        const val = getValueFromPosition(ev.clientX);
        if (thumb === "from") {
          onChange(Math.min(val, valueTo), valueTo);
        } else {
          onChange(valueFrom, Math.max(val, valueFrom));
        }
      }

      function onPointerUp() {
        target.removeEventListener("pointermove", onPointerMove);
        target.removeEventListener("pointerup", onPointerUp);
      }

      target.addEventListener("pointermove", onPointerMove);
      target.addEventListener("pointerup", onPointerUp);
    };
  }

  return (
    <div className="relative w-full h-5 flex items-center" ref={trackRef}>
      <div className="absolute left-0 right-0 h-1.5 rounded-full surface-brand-subtle" />
      <div
        className="absolute h-1.5 rounded-full bg-brand-500/30"
        style={{ left: `${leftPercent}%`, right: `${100 - rightPercent}%` }}
      />
      <div
        className="absolute w-4 h-4 rounded-full primary-normal border surface-brand-stroke cursor-grab active:cursor-grabbing touch-none z-10"
        style={{ left: `${leftPercent}%`, transform: "translateX(-50%)" }}
        onPointerDown={handlePointerDown("from")}
      />
      <div
        className="absolute w-4 h-4 rounded-full primary-normal border surface-brand-stroke cursor-grab active:cursor-grabbing touch-none z-10"
        style={{ left: `${rightPercent}%`, transform: "translateX(-50%)" }}
        onPointerDown={handlePointerDown("to")}
      />
    </div>
  );
}
